import CloseIcon from '@mui/icons-material/Close';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { StateDisplay } from '@/components/common';
import { ROUTES } from '@/constants/routes';
import { useNotificationMutations } from '@/features/notifications/hooks/useNotificationMutations';
import { useNotifications } from '@/features/notifications/hooks/useNotifications';
import type {
  Notification,
  NotificationType,
} from '@/features/notifications/types/notifications.types';
import { useToast } from '@/hooks/useToast';
import { motion } from '@/theme/tokens';
import { formatShortDate } from '@/utils/dateUtils';

export interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
}

const DRAWER_TITLE_ID = 'notification-drawer-title';

/** A representative icon per notification category — a small visual anchor on each row. */
function iconFor(type: NotificationType): ReactNode {
  switch (type) {
    case 'INTERVIEW_SCHEDULED':
    case 'INTERVIEW_REMINDER':
      return <EventOutlinedIcon fontSize="small" />;
    case 'APPLICATION_STATUS_CHANGED':
    case 'APPLICATION_STALLED':
    case 'JOB_POSTED':
      return <WorkOutlineIcon fontSize="small" />;
    default:
      return <NotificationsNoneOutlinedIcon fontSize="small" />;
  }
}

/** Compact "time ago" for a notification's createdAt — falls back to a date past a week. */
function timeAgo(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatShortDate(iso);
}

/**
 * The bell-triggered notification panel shared by every persona's `TopNav`
 * (`@willnq/shared/design-system` `notificationCenterSpec`). Backed by the real `notifications` query
 * (ADR-0020): a paginated read the client refetches on open — no polling, no subscription (live push
 * is deferred until the Gateway standardises WebSocket auth). Clicking an unread row marks it read and
 * follows its deep link; "Mark all read" clears the badge in one call. The Drawer shell, header and
 * close behavior are unchanged from the placeholder.
 */
export function NotificationDrawer({ open, onClose }: NotificationDrawerProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { notifications, loading, error } = useNotifications(open);
  const { markRead, markAllRead, markingAll } = useNotificationMutations();

  const hasUnread = notifications.some((n) => !n.isRead);

  const openNotification = async (notification: Notification) => {
    if (!notification.isRead) {
      const errors = await markRead(notification.id);
      if (errors.length > 0) {
        toast.showError(errors[0].message);
        return;
      }
    }
    if (notification.deepLink) {
      onClose();
      navigate(notification.deepLink);
    }
  };

  const handleMarkAllRead = async () => {
    const errors = await markAllRead();
    if (errors.length > 0) toast.showError(errors[0].message);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: motion.duration.medium, exit: motion.duration.short }}
      ModalProps={{ keepMounted: true }}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 380 } } } }}
      aria-labelledby={DRAWER_TITLE_ID}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pl: 3, pr: 2, py: 2 }}
      >
        <Typography variant="h6" component="h2" id={DRAWER_TITLE_ID}>
          Notifications
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {hasUnread ? (
            <Button size="small" onClick={handleMarkAllRead} disabled={markingAll}>
              Mark all read
            </Button>
          ) : null}
          <IconButton onClick={onClose} aria-label="Close notifications" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Divider />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} aria-label="Loading notifications" />
        </Box>
      ) : error ? (
        <StateDisplay
          dense
          tone="error"
          icon={<NotificationsNoneOutlinedIcon />}
          title="Couldn't load notifications"
          description="Something went wrong loading your notifications. Please try again."
        />
      ) : notifications.length === 0 ? (
        <StateDisplay
          dense
          tone="secondary"
          icon={<NotificationsNoneOutlinedIcon />}
          title="You're all caught up"
          description="You have no notifications right now. We'll let you know when something needs your attention."
        />
      ) : (
        <List sx={{ py: 0 }}>
          {notifications.map((notification) => (
            <ListItemButton
              key={notification.id}
              onClick={() => openNotification(notification)}
              aria-label={`${notification.title}${notification.isRead ? '' : ' (unread)'}`}
              sx={{ alignItems: 'flex-start', py: 1.5, px: 3, gap: 0.5 }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  mt: 0.25,
                  color: notification.isRead ? 'text.secondary' : 'primary.main',
                }}
              >
                {iconFor(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={notification.title}
                secondary={
                  <>
                    <Typography variant="caption" color="text.secondary" component="span">
                      {notification.body}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      component="span"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {timeAgo(notification.createdAt)}
                    </Typography>
                  </>
                }
                slotProps={{
                  primary: {
                    variant: 'body2',
                    fontWeight: notification.isRead ? 400 : 700,
                  },
                }}
              />
              {/* Unread indicator — a primary dot, paired with the bold title above (never
                  color-only) so it reads for everyone (notificationCenterSpec accessibility). */}
              {!notification.isRead ? (
                <Box
                  aria-hidden
                  sx={{
                    mt: 1,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    flexShrink: 0,
                  }}
                />
              ) : null}
            </ListItemButton>
          ))}
        </List>
      )}

      {/* Footer link to the full history page (M-5) — the drawer only shows the newest page. */}
      {!loading && !error ? (
        <>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <Button
              size="small"
              onClick={() => {
                onClose();
                navigate(ROUTES.notificationsHistory);
              }}
            >
              View all notifications
            </Button>
          </Box>
        </>
      ) : null}
    </Drawer>
  );
}
